import { useState } from 'react';
import useSWRMutation from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import { type NetworkType } from '@mls-wallet/shared-types';
import { type AccountProposal } from '../account/use-account-proposals';

type CreateAccountProposalInput = {
  name: string;
  networkIds: NetworkType[];
};

interface UseCreateAccountProposalReturn {
  createProposal: (
    input: CreateAccountProposalInput
  ) => Promise<AccountProposal>;
  isCreating: boolean;
  createError: string | null;
}

// Fetcher for creating proposals
const createProposalFetcher = async (
  _key: string,
  { arg }: { arg: CreateAccountProposalInput }
): Promise<AccountProposal> => {
  return await denApi.post<AccountProposal, CreateAccountProposalInput>(
    'admin/multichain-account/proposals',
    arg
  );
};

/**
 * Hook for creating account proposals
 */
export function useCreateAccountProposal(): UseCreateAccountProposalReturn {
  const [createError, setCreateError] = useState<string | null>(null);

  // SWR mutation for creating proposals
  const { trigger: triggerCreateProposal, isMutating: isCreating } =
    useSWRMutation<AccountProposal, Error, string, CreateAccountProposalInput>(
      'create-proposal',
      createProposalFetcher
    );

  /**
   * Create a new account proposal
   * @param input - The proposal input data
   * @returns Promise that resolves to the created proposal
   */
  const createProposal = async (
    input: CreateAccountProposalInput
  ): Promise<AccountProposal> => {
    setCreateError(null);

    try {
      const proposal = await triggerCreateProposal(input);
      return proposal;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create proposal';
      setCreateError(errorMessage);
      throw err;
    }
  };

  return {
    createProposal,
    isCreating,
    createError,
  };
}
